import {Box, Typography} from '@mui/material';
import {
	AbandonFlowModal,
	FailToCompleteActionModal,
	StepProgressBar,
} from 'presentation/components';
import {CustomStepper} from 'presentation/components/CustomStepper';
import {MainHeader} from 'presentation/components/MainHeader';
import {PageWrapperLayout} from 'presentation/components/PageWrapperLayout';
import {emailRegexValidation, OperationStateTypesEnum} from 'presentation/constants';
import {usePersonalData} from 'presentation/hooks';
import {useMediaQueries} from 'presentation/pages/private/hooks';
import {BeneficiaryData} from 'presentation/pages/private/refund/components';
import {RefundVariantEnum} from 'presentation/pages/private/refund/constants/refundVariantEnum';
import {
	useBackButtonExitModal,
	useSendAdditionalDocs,
} from 'presentation/pages/private/refund/hooks';
import {translations} from 'presentation/translations-new/';
import React, {Fragment, useLayoutEffect, useState} from 'react';
import {useLocation} from 'react-router-dom';
import {ConclusionModal} from '../../components/ConclusionModal';
import {useResetRefundFlow} from '../../hooks/useResetRefundFlow';
import type {RefundFlowNavigationState} from '../../types/refundFlowNavigationState';
import {Conference, SearchProtocol, SendDocuments} from './components';
import {useQueryParamsSync} from './hooks/useQueryParamsSync';
import {useStepSendDocsStore} from './store/useStepSendDocsStore';
import {CustomStack} from './styles';
import {FormStepEnum} from './types';

const {tryAgain, goToHome} = translations['pt-br'].commons;
const {title, discardModal} = translations['pt-br'].privateFlow.refund.additionalDocuments;
const {
	title: errorTitle,
	description: errorDescription,
	headerTitle: errorHeaderTitle,
} = translations['pt-br'].privateFlow.refund.additionalDocuments.errorModal;

export function SendAdditionalDocs() {
	const {resetRefundFlow} = useResetRefundFlow();
	const {isMobile} = useMediaQueries();
	const {state: locationState} = useLocation();
	const {currentEmail, currentPhone} = usePersonalData();
	const [{additionalDocsReference, refundTermsReturnTo}] = useState(
		() => (locationState ?? {}) as RefundFlowNavigationState,
	);
	const formStep = useStepSendDocsStore(state => state.step);
	const setFormStep = useStepSendDocsStore(state => state.setStep);
	const isContactDataValid =
		Boolean(currentPhone) && emailRegexValidation.test(currentEmail || '');
	const {
		exitFlowModalState,
		setExitFlowModalState,
		handleBack,
		handleNextStep,
		setConcludeRequestSendAdditionalDocsState,
		IS_ERROR,
		IS_LOADING,
		formStepsLabels,
		formStepsTexts,
		handleClose,
		handleAbandonFlowModalAction,
		handleGoToHome,
		conclusionModalState,
		setConclusionModalState,
		protocol,
	} = useSendAdditionalDocs({refundTermsReturnTo});

	useQueryParamsSync({protocol});

	const formStepByStep = {
		[FormStepEnum.BENEFICIARY_DATA]: (
			<BeneficiaryData
				handleNextStep={handleNextStep}
				variant={RefundVariantEnum.ADDITIONAL_DOCUMENTS}
			/>
		),
		[FormStepEnum.SEARCH_PROTOCOL]: (
			<SearchProtocol autoSearchReference={additionalDocsReference} />
		),
		[FormStepEnum.SENDING_FILES]: <SendDocuments />,
		[FormStepEnum.CONFERENCE]: (
			<Conference handleNextStep={handleNextStep} isLoading={IS_LOADING} />
		),
	};

	useBackButtonExitModal({
		setExitModal: setExitFlowModalState,
		shouldOpenModal: formStep !== FormStepEnum.BENEFICIARY_DATA,
	});

	useLayoutEffect(() => {
		resetRefundFlow();

		if (additionalDocsReference && isContactDataValid) {
			setFormStep(FormStepEnum.SEARCH_PROTOCOL);
		}
	}, []);

	return (
		<PageWrapperLayout title={title}>
			<Fragment>
				<MainHeader
					title={title}
					handleBack={handleBack}
					variant="subRoute"
					handleClose={handleClose}>
					<CustomStack spacing={2}>
						{isMobile ? (
							<StepProgressBar
								currentStep={formStep + 1}
								totalSteps={formStepsLabels.length}
							/>
						) : (
							<Box pb={1}>
								<CustomStepper activeStep={formStep} stepLabels={formStepsLabels} />
							</Box>
						)}
						<Typography variant="h6">{formStepsTexts[formStep]}</Typography>
					</CustomStack>
				</MainHeader>
				{formStepByStep[formStep]}

				<ConclusionModal
					variant={RefundVariantEnum.ADDITIONAL_DOCUMENTS}
					protocol={protocol}
					open={conclusionModalState}
					setOpen={setConclusionModalState}
				/>

				<AbandonFlowModal
					open={exitFlowModalState}
					setOpen={setExitFlowModalState}
					handleConfirm={handleAbandonFlowModalAction}
					title={discardModal.title}
					headerTitle={discardModal.headerTitle}
					description={discardModal.description}
				/>

				<FailToCompleteActionModal
					headerTitle={errorHeaderTitle}
					description={errorDescription}
					title={errorTitle}
					open={IS_ERROR}
					setOpen={() =>
						setConcludeRequestSendAdditionalDocsState(OperationStateTypesEnum.NONE)
					}
					primaryActionText={tryAgain}
					secondaryActionText={goToHome}
					fallbackAction={handleGoToHome}
					handleTryAgain={handleNextStep}
				/>
			</Fragment>
		</PageWrapperLayout>
	);
}
